// VolunteerPetProfileOptions.tsx
import React from 'react'
import { Box } from '@mui/material'
import { EmtTypography, EmtSeparator, EmtCheckbox } from 'emt-components-v2'
import SwitchChoices from '@/components/Forms/SwitchChoices'
import { useTranslation } from 'next-i18next'

import { usePetsittingMode } from '@/store/usePetsittingMode'

export interface VolunteerPetProfileOptionsProps {
  showVolunteer: boolean
  onToggleVolunteer: () => void
  volunteerChoices: {
    times: Record<string, boolean>
    availability: Record<string, boolean>
    needs: Record<string, boolean>
  }
  handleVolunteerChoice: (category: 'times' | 'availability' | 'needs', slug: string, value: boolean) => void
  timeChoices: Array<{ slug: string; name: string; description?: string }>
  availabilityChoices: Array<{ slug: string; name: string }>
  needsChoices: Array<{ slug: string; name: string; iconClass?: string }>
  errors?: Record<string, string>
}

const VolunteerPetProfileOptions: React.FC<VolunteerPetProfileOptionsProps> = ({
  showVolunteer,
  onToggleVolunteer,
  volunteerChoices,
  handleVolunteerChoice,
  timeChoices,
  availabilityChoices,
  needsChoices,
  errors,
}) => {
  const { t } = useTranslation('connection')
  const { isPetsittingMode } = usePetsittingMode()
  return (
    <Box
      sx={{
        padding: isPetsittingMode ? '14px 16px' : '0',
        borderRadius: isPetsittingMode ? '16px' : '0',
        border: isPetsittingMode ? '1px solid #E8E8EA' : 'none',
      }}
    >
      {isPetsittingMode && (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px', cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation()
            onToggleVolunteer()
          }}
        >
          <EmtCheckbox
            sx={{
              '& .emt-icons': {
                width: '18px',
                height: '18px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(!showVolunteer && { borderColor: '#D2D1D4' }),
              },
            }}
            checked={showVolunteer}
            size="medium"
            variant="primary"
          />
          <Box sx={{ lineHeight: '1' }}>
            <EmtTypography variant="body2" fontWeight="700" lineHeight="1">
              Montrer son profil aux emprunteurs bénévoles
            </EmtTypography>
            <EmtTypography variant="caption" lineHeight="1" color="#77757F">
              Nous proposons des pet sitters ainsi que des emprunteurs bénévoles pour vous donner un maximum de
              solutions pour la garde et le bien-être de vos animaux
            </EmtTypography>
          </Box>
        </Box>
      )}
      {showVolunteer && (
        <>
          {isPetsittingMode && <EmtSeparator sx={{ marginY: '20px' }} />}
          <Box>
            <SwitchChoices
              title={t('create-your-profile-petowner.pet.timeChoices')}
              choices={timeChoices}
              selectedChoices={volunteerChoices.times}
              handleChoice={(slug, value) => handleVolunteerChoice('times', slug, value)}
              color="primary"
            />
            {errors?.times && (
              <EmtTypography variant="caption" sx={{ mt: 1 }} color="error.main">
                {errors.times}
              </EmtTypography>
            )}
          </Box>
          <EmtSeparator sx={{ marginY: '20px' }} />
          <Box>
            <SwitchChoices
              title={t('create-your-profile-petowner.pet.availabilityChoices')}
              choices={availabilityChoices}
              selectedChoices={volunteerChoices.availability}
              handleChoice={(slug, value) => handleVolunteerChoice('availability', slug, value)}
              color="primary"
            />
            {errors?.availability && (
              <EmtTypography variant="caption" sx={{ mt: 1 }} color="error.main">
                {errors.availability}
              </EmtTypography>
            )}
          </Box>
          <EmtSeparator sx={{ marginY: '20px' }} />
          <Box>
            <SwitchChoices
              title={t('create-your-profile-petowner.pet.needsChoices')}
              choices={needsChoices}
              selectedChoices={volunteerChoices.needs}
              handleChoice={(slug, value) => handleVolunteerChoice('needs', slug, value)}
              color="primary"
            />
            {errors?.needs && (
              <EmtTypography variant="caption" sx={{ mt: 1 }} color="error.main">
                {errors.needs}
              </EmtTypography>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default VolunteerPetProfileOptions
